// =============================================================================
// Revenue Planner Engine — Pure Math Functions
// =============================================================================
// PURPOSE: Bidirectional funnel calculator for revenue goal planning.
// All functions are pure (no React, no side effects) for easy testing.
//
// TWO DIRECTIONS:
//   Reverse: goal → required inputs per source (left panel: "What We Need")
//   Forward: input overrides → projected cash (right panel: "What Happens If")
//
// THREE SOURCES: webinar, typeform, direct (cold DMs)
// Each source has its own conversion chain:
//   leads → (× book_rate) → booked → (× show_rate) → shown → (× close_rate) → closed → (× aov) → cash
// =============================================================================

export type SourceKey = "webinar" | "typeform" | "dm";

export const SOURCE_KEYS: SourceKey[] = ["webinar", "typeform", "dm"];

export const SOURCE_LABELS: Record<SourceKey, string> = {
  webinar: "Webinar",
  typeform: "Typeform",
  dm: "DMs",
};

export const SOURCE_ICONS: Record<SourceKey, string> = {
  webinar: "\uD83C\uDFA5",
  typeform: "\uD83D\uDCCB",
  dm: "\uD83D\uDCAC",
};

// --- Types ---

export interface SourceRates {
  leads_in: number;
  booked: number;
  shown: number;
  closed: number;
  revenue: number;
  cash: number;
  book_rate: number; // booked / leads_in
  show_rate: number; // shown / booked
  close_rate: number; // closed / shown
  aov: number; // cash / closed
  attendance_rate: number; // attended / total_signups (webinar only, 0 for others)
  total_signups?: number; // raw count for webinar slider max
}

export interface RateOverrides {
  book_rate?: number; // 0-1 fraction
  show_rate?: number;
  close_rate?: number;
  aov?: number;
  attendance_rate?: number; // webinar only
}

export interface MonthMetrics {
  days_elapsed: number;
  days_remaining: number;
  days_in_month: number;
  revenue_so_far: number;
  cash_so_far: number;
}

export interface ReverseCalcResult {
  source_goal: number; // $ allocated to this source
  source_share: number; // 0-1 proportion
  needed_closed: number;
  needed_shown: number;
  needed_booked: number;
  needed_leads: number;
  needed_leads_per_day: number;
  actual_leads_per_day: number;
  gap_leads_per_day: number; // positive = need more, negative = ahead
}

export interface ForwardCalcResult {
  projected_leads: number;
  projected_booked: number;
  projected_shown: number;
  projected_closed: number;
  projected_cash: number;
  projected_attended?: number; // webinar only
}

export interface PlannerProjection {
  bySource: Record<SourceKey, ForwardCalcResult>;
  total_projected: number; // cash_so_far + sum of projected
  gap_to_goal: number; // goal - total_projected (positive = shortfall)
  pct_to_goal: number; // 0-100+
}

export type PaceStatus = "on_track" | "behind" | "ahead" | "no_goal";

// --- Derived Rates ---

/** Compute conversion rates from raw funnel counts */
export function deriveRates(source: {
  leads_in: number;
  booked: number;
  shown: number;
  closed: number;
  revenue: number;
  cash: number;
  attended?: number;
  total_signups?: number;
}): SourceRates {
  return {
    ...source,
    book_rate: source.leads_in > 0 ? source.booked / source.leads_in : 0,
    show_rate: source.booked > 0 ? source.shown / source.booked : 0,
    close_rate: source.shown > 0 ? source.closed / source.shown : 0,
    aov: source.closed > 0 ? source.cash / source.closed : 0,
    attendance_rate:
      (source.total_signups ?? 0) > 0 ? (source.attended ?? 0) / (source.total_signups ?? 1) : 0,
  };
}

// --- Source Distribution ---

/** Get revenue share per source (proportional to actual cash) */
function getSourceDistribution(
  rates: Partial<Record<SourceKey, SourceRates>>,
  activeSources: SourceKey[]
): Record<SourceKey, number> {
  const result: Record<SourceKey, number> = { webinar: 0, typeform: 0, dm: 0 };

  const totalCash = activeSources.reduce((sum, key) => sum + (rates[key]?.cash ?? 0), 0);

  if (totalCash === 0) {
    // Equal split among active sources
    const share = activeSources.length > 0 ? 1 / activeSources.length : 0;
    for (const key of activeSources) {
      result[key] = share;
    }
    return result;
  }

  for (const key of activeSources) {
    result[key] = (rates[key]?.cash ?? 0) / totalCash;
  }

  return result;
}

// --- Reverse Calculation (Goal → Required Inputs) ---

/** For one source: given a revenue target + rates, work backwards to required leads/day */
function reverseCalcSource(
  sourceGoal: number,
  rates: SourceRates,
  daysInMonth: number,
  daysElapsed: number
): ReverseCalcResult {
  const share = 0; // filled by caller

  if (rates.aov === 0 || rates.close_rate === 0 || rates.show_rate === 0 || rates.book_rate === 0) {
    return {
      source_goal: sourceGoal,
      source_share: share,
      needed_closed: 0,
      needed_shown: 0,
      needed_booked: 0,
      needed_leads: 0,
      needed_leads_per_day: 0,
      actual_leads_per_day: daysElapsed > 0 ? Math.ceil(rates.leads_in / daysElapsed) : 0,
      gap_leads_per_day: 0,
    };
  }

  const neededClosed = sourceGoal / rates.aov;
  const neededShown = neededClosed / rates.close_rate;
  const neededBooked = neededShown / rates.show_rate;
  const neededLeads = neededBooked / rates.book_rate;
  // Use full month — this shows the ideal AVERAGE pace for the whole month
  const neededLeadsPerDay = daysInMonth > 0 ? neededLeads / daysInMonth : 0;
  const actualLeadsPerDay = daysElapsed > 0 ? rates.leads_in / daysElapsed : 0;

  return {
    source_goal: sourceGoal,
    source_share: share,
    needed_closed: Math.ceil(neededClosed),
    needed_shown: Math.ceil(neededShown),
    needed_booked: Math.ceil(neededBooked),
    needed_leads: Math.ceil(neededLeads),
    needed_leads_per_day: Math.ceil(neededLeadsPerDay),
    actual_leads_per_day: Math.ceil(actualLeadsPerDay),
    gap_leads_per_day: Math.ceil(neededLeadsPerDay) - Math.ceil(actualLeadsPerDay),
  };
}

/** Reverse calc across all sources: distribute goal proportionally, then calc each */
export function reverseCalculate(
  goal: number,
  rates: Partial<Record<SourceKey, SourceRates>>,
  monthMetrics: MonthMetrics,
  activeSources: SourceKey[]
): Record<SourceKey, ReverseCalcResult> {
  const distribution = getSourceDistribution(rates, activeSources);
  const result = {} as Record<SourceKey, ReverseCalcResult>;

  for (const key of SOURCE_KEYS) {
    if (!activeSources.includes(key) || !rates[key]) {
      result[key] = {
        source_goal: 0,
        source_share: 0,
        needed_closed: 0,
        needed_shown: 0,
        needed_booked: 0,
        needed_leads: 0,
        needed_leads_per_day: 0,
        actual_leads_per_day: 0,
        gap_leads_per_day: 0,
      };
      continue;
    }

    const sourceGoal = goal * distribution[key];
    const calc = reverseCalcSource(
      sourceGoal,
      rates[key],
      monthMetrics.days_in_month,
      monthMetrics.days_elapsed
    );
    calc.source_share = distribution[key];
    result[key] = calc;
  }

  return result;
}

// --- Forward Projection (Input Overrides → Projected Revenue) ---

/** For one source: given leads/day override + rates, project forward */
function forwardCalcSource(
  leadsPerDay: number,
  rates: SourceRates,
  daysRemaining: number,
  overrides?: RateOverrides
): ForwardCalcResult {
  const bookRate = overrides?.book_rate ?? rates.book_rate;
  const showRate = overrides?.show_rate ?? rates.show_rate;
  const closeRate = overrides?.close_rate ?? rates.close_rate;
  const aov = overrides?.aov ?? rates.aov;

  const projectedLeads = leadsPerDay * daysRemaining;
  const projectedBooked = projectedLeads * bookRate;
  const projectedShown = projectedBooked * showRate;
  const projectedClosed = projectedShown * closeRate;
  const projectedCash = projectedClosed * aov;

  return {
    projected_leads: Math.round(projectedLeads),
    projected_booked: Math.round(projectedBooked),
    projected_shown: Math.round(projectedShown),
    projected_closed: Math.round(projectedClosed),
    projected_cash: Math.round(projectedCash),
  };
}

/**
 * Webinar-specific forward calc.
 * Chain: totalRegistrants × attendance_rate → attended × book_rate → booked
 *        × show_rate → shown × close_rate → closed × aov → cash
 */
export function forwardCalcWebinar(
  totalRegistrants: number,
  rates: SourceRates,
  overrides?: RateOverrides
): ForwardCalcResult {
  const attendanceRate = overrides?.attendance_rate ?? rates.attendance_rate;
  const bookRate = overrides?.book_rate ?? rates.book_rate;
  const showRate = overrides?.show_rate ?? rates.show_rate;
  const closeRate = overrides?.close_rate ?? rates.close_rate;
  const aov = overrides?.aov ?? rates.aov;

  const projectedAttended = totalRegistrants * attendanceRate;
  const projectedBooked = projectedAttended * bookRate;
  const projectedShown = projectedBooked * showRate;
  const projectedClosed = projectedShown * closeRate;
  const projectedCash = projectedClosed * aov;

  return {
    projected_leads: Math.round(totalRegistrants),
    projected_attended: Math.round(projectedAttended),
    projected_booked: Math.round(projectedBooked),
    projected_shown: Math.round(projectedShown),
    projected_closed: Math.round(projectedClosed),
    projected_cash: Math.round(projectedCash),
  };
}

/** Forward projection across all sources */
export function forwardProject(
  inputOverrides: Partial<Record<SourceKey, number>>, // leads_per_day per source (registrants for webinar)
  rates: Partial<Record<SourceKey, SourceRates>>,
  monthMetrics: MonthMetrics,
  activeSources: SourceKey[],
  rateOverrides?: Partial<Record<SourceKey, RateOverrides>> // optional per-source rate overrides
): PlannerProjection {
  const bySource = {} as Record<SourceKey, ForwardCalcResult>;
  let sumProjected = 0;

  for (const key of SOURCE_KEYS) {
    if (!activeSources.includes(key) || !rates[key]) {
      bySource[key] = {
        projected_leads: 0,
        projected_booked: 0,
        projected_shown: 0,
        projected_closed: 0,
        projected_cash: 0,
      };
      continue;
    }

    const sourceRates = rates[key];
    const sourceRateOverrides = rateOverrides?.[key];

    let calc: ForwardCalcResult;

    if (key === "webinar") {
      // Webinar: inputOverrides["webinar"] = total registrants (not leads/day)
      const totalRegistrants =
        inputOverrides[key] ?? sourceRates.total_signups ?? sourceRates.leads_in;
      calc = forwardCalcWebinar(totalRegistrants, sourceRates, sourceRateOverrides);
    } else {
      const leadsPerDay =
        inputOverrides[key] ??
        (monthMetrics.days_elapsed > 0 ? sourceRates.leads_in / monthMetrics.days_elapsed : 0);
      calc = forwardCalcSource(
        leadsPerDay,
        sourceRates,
        monthMetrics.days_remaining,
        sourceRateOverrides
      );
    }

    bySource[key] = calc;
    sumProjected += calc.projected_cash;
  }

  const totalProjected = monthMetrics.cash_so_far + sumProjected;

  return {
    bySource,
    total_projected: Math.round(totalProjected),
    gap_to_goal: 0, // filled by caller with goal
    pct_to_goal: 0, // filled by caller with goal
  };
}

/** Attach goal-relative metrics to a projection */
export function withGoal(projection: PlannerProjection, goal: number): PlannerProjection {
  return {
    ...projection,
    gap_to_goal: goal > 0 ? Math.round(goal - projection.total_projected) : 0,
    pct_to_goal: goal > 0 ? Math.round((projection.total_projected / goal) * 1000) / 10 : 0,
  };
}

// --- Pace Status ---

/** Determine if the org is on track, behind, or ahead of goal pace */
export function computePaceStatus(
  cashSoFar: number,
  goal: number,
  daysElapsed: number,
  daysInMonth: number
): PaceStatus {
  if (goal <= 0) return "no_goal";
  if (daysElapsed === 0) return "on_track";

  const expectedPace = (daysElapsed / daysInMonth) * goal;
  const ratio = cashSoFar / expectedPace;

  if (ratio >= 1.1) return "ahead";
  if (ratio >= 0.8) return "on_track";
  return "behind";
}

/** Get current-pace projected total (linear extrapolation from actual) */
export function paceProjection(
  cashSoFar: number,
  daysElapsed: number,
  daysInMonth: number
): number {
  if (daysElapsed === 0) return 0;
  return Math.round((cashSoFar / daysElapsed) * daysInMonth);
}
